import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, Subject } from 'rxjs';
import { map, tap } from 'rxjs';

import { SAP_UNAVAILABLE_ERROR_CODES, SAP_UNAVAILABLE_MESSAGE_PARTIALS } from '@shared-constants/odata-constants';
import { LocalStorageKeyEnum } from '@shared-enums/local-storage.enum';
import { TimesheetStatusEnum } from '@shared-enums/time.enum';
import { arrayIsTruthy } from '@shared-helpers/array-object-helpers';
import { checkLocalStorage } from '@shared-helpers/storage-helpers';
import * as sharedApi from '@shared-interfaces/api';
import { IOutgoingCatRecords } from '@shared-interfaces/cats-db';
import { IExtDocument } from '@shared-interfaces/documents';
import { IHttpErrorResponseObject } from '@shared-interfaces/error';

import { environment } from '../../environments/environment';
import { ICatRecord } from '../interfaces/incoming';
import * as internal from '../interfaces/internal-interfaces';
import { ISapRequestFilterQueryProps } from './api.model';
import { LogRocketService } from './log-rocket.service';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  private readonly catsRecordsSearchedSubject$ = new Subject<ICatRecord[]>();
  public readonly catsRecordsSearched$ = this.catsRecordsSearchedSubject$.asObservable();

  private readonly timberTrapUrl = environment.timberTrapUrl;
  private readonly employeeManagerCache: Record<string, sharedApi.IHttpResponseEmployeeManager> = {};

  constructor(
    private readonly http: HttpClient,
    private readonly logRocketService: LogRocketService,
  ) {}

  public sendEmail$(message: string, recipientEmail: string, subject: string): Observable<sharedApi.ISimpleEmailResponse> {
    return this.http.post<sharedApi.ISimpleEmailResponse>(`${this.timberTrapUrl}simpleEmail`, {
      message,
      recipientEmail,
      subject,
    });
  }

  public getEmployees$(queryParams: ISapRequestFilterQueryProps): Observable<sharedApi.IHttpResponseEmployee[]> {
    const fullUrl = this.makeUrl('getEmployees');
    const params = new HttpParams().set('$expand', 'EmpToActivity').set('$filter', this.makeFilterByObj(queryParams));
    return this.http.get<sharedApi.IHttpResponseEmployee[]>(fullUrl, { params }).pipe(map((res) => res ?? []));
  }

  public getOneEmployee$(
    date: string,
    employeeNum: string,
    shouldCheckValidity = true,
  ): Observable<sharedApi.IHttpResponseEmployee | null> {
    const fullUrl = this.makeUrl(`employee/${date}/${employeeNum}`);
    const params = new HttpParams().set('check', shouldCheckValidity);
    return this.http.get<sharedApi.IHttpResponseEmployee>(fullUrl, { params });
  }

  public getSharepointFiles$(
    costCenter: string,
    weekendingDate: string,
    custGrp: string,
  ): Observable<sharedApi.IHttpResponseSharePointFilesList[]> {
    const fullUrl = this.makeUrl('sharepoint/files/list');
    return this.http
      .post<sharedApi.IHttpResponseSharePointFilesList[]>(fullUrl, { costCenter, weekendingDate, custGrp })
      .pipe(map((res) => res ?? []));
  }

  public uploadSharepointFile$(
    costCenter: string,
    weekendingDate: string,
    custGrp: string,
    name: string,
    data: string,
  ): Observable<sharedApi.IHttpResponseSharePointFile> {
    const fullUrl = this.makeUrl('sharepoint/files');
    return this.http.post<sharedApi.IHttpResponseSharePointFile>(fullUrl, { costCenter, weekendingDate, custGrp, name, data });
  }

  public deleteSharepointFiles$(costCenter: string, weekendingDate: string, custGrp: string, name: string): Observable<boolean> {
    const fullUrl = this.makeUrl('sharepoint/files');
    return this.http.delete<boolean>(fullUrl, { body: { costCenter, weekendingDate, custGrp, name } });
  }

  public updateSharepointFile$(
    costCenter: string,
    weekendingDate: string,
    custGrp: string,
    oldName: string,
    newName: string,
  ): Observable<sharedApi.IHttpResponseSharePointFile> {
    const fullUrl = this.makeUrl('sharepoint/files');
    return this.http.patch<sharedApi.IHttpResponseSharePointFile>(fullUrl, {
      costCenter,
      weekendingDate,
      custGrp,
      oldName,
      newName,
    });
  }

  public getSelf$(): Observable<sharedApi.IHttpResponseUser> {
    const fullUrl = this.makeUrl('whoami');
    return this.http.get<sharedApi.IHttpResponseUser>(fullUrl, { withCredentials: true });
  }

  public getApprovers$(): Observable<sharedApi.IWebServerResponseApproverList[]> {
    const fullUrl = this.makeUrl('approver');
    return this.http.get<sharedApi.IWebServerResponseApproverList[]>(fullUrl).pipe(map((res) => res ?? []));
  }

  public getContracts$(
    queryParams: ISapRequestFilterQueryProps | internal.IComplexFilterItem[],
  ): Observable<sharedApi.IHttpResponseContract[]> {
    const fullUrl = this.makeUrl('getContracts');
    const filterObj = Array.isArray(queryParams) ? this.makeComplexFilterByObj(queryParams) : this.makeFilterByObj(queryParams);
    const params = new HttpParams().set('$filter', filterObj);
    return this.http.get<sharedApi.IHttpResponseContract[]>(fullUrl, { params }).pipe(map((res) => res ?? []));
  }

  public getCats$(
    queryParams: ISapRequestFilterQueryProps,
    options?: {
      select?: string;
    },
  ): Observable<ICatRecord[]> {
    const filter = this.makeFilterByObj(queryParams);
    const defaultSelect =
      'CatsHeadtoItem/Counter,CatsHeadtoItem/Workdate,CatsHeadtoItem/Employeenumber,CatsHeadtoItem/SendCctr,CatsHeadtoItem/Acttype,CatsHeadtoItem/RecCctr,CatsHeadtoItem/RecOrder,CatsHeadtoItem/AbsAttType,CatsHeadtoItem/Catshours,CatsHeadtoItem/Shorttext,CatsHeadtoItem/Extdocumentno,CatsHeadtoItem/Status,CatsHeadtoItem/Statkeyfig,CatsHeadtoItem/Quantity,CatsHeadtoItem/Longtext,CatsHeadtoItem/Zequipment,CatsHeadtoItem/ZempCreatedBy,CatsHeadtoItem/Zmatnr,CatsHeadtoItem/ModifiedEmpno,CatsHeadtoItem/LongTextstr,CatsHeadtoItem/ZprdGrpID,CatsHeadtoItem/Zperdiemamount,CatsHeadtoItem/Zrecordtype';
    const selectString = options?.select ?? defaultSelect;
    const params = new HttpParams().set('$expand', 'CatsHeadtoItem').set('$filter', filter).set('$select', selectString); //we could still enable flexible selection, but I prefer standardization for now
    const fullUrl = this.makeUrl('getCats');
    return this.http.get<sharedApi.ICatsHeadToIemHttpGetResultsObj<ICatRecord[]>>(fullUrl, { params }).pipe(
      map((value) => {
        const arr = value[0]?.CatsHeadtoItem.results ?? [];
        this.catsRecordsSearchedSubject$.next(arr);
        return arr;
      }),
    );
  }

  public getProductionGroupId$(): Observable<string> {
    const fullUrl = this.makeUrl('prodGroup');
    return this.http.get<sharedApi.IHttpResponseGetProductionGroupId>(fullUrl).pipe(map((res) => res.GetProdGroupID.Prdgrpid));
  }

  public deleteProductionGroup$(id: string): Observable<boolean> {
    const fullUrl = this.makeUrl('prodGroup');
    return this.http.delete<boolean>(fullUrl, { body: { Documentno: id } });
  }

  public getContractConditions$(queryParams: ISapRequestFilterQueryProps): Observable<sharedApi.ISapResponseContractCondition[]> {
    const fullUrl = this.makeUrl('getContractConditions');
    const params = new HttpParams()
      .set('$filter', this.makeFilterByObj(queryParams))
      .set('$format', 'json')
      .set('$expand', 'CondToSkf,CondToSkf/SkfToValue,CondToMat,CondToDefault,CondToTimesheetHdr/TimesheetConfigValuesSet');
    return this.http.get<sharedApi.ISapResponseContractCondition[]>(fullUrl, { params }).pipe(map((res) => res ?? []));
  }

  public postRecords$(recordsObj: IOutgoingCatRecords): Observable<ICatRecord[]> {
    if (!recordsObj) return of([]);
    if (!arrayIsTruthy(recordsObj.d.CatsHeadtoItem) && !arrayIsTruthy(recordsObj.d.CatsHeadtoTso ?? [])) return of([]);
    const fullUrl = this.makeUrl('catsdb');
    return this.http
      .post<sharedApi.ICatsHeadToItemHttpPostResultsObj<ICatRecord[]>>(fullUrl, recordsObj)
      .pipe(map((value) => value.CatsHeadtoItem.results ?? []));
  }

  public getEquipment$(queryParams: ISapRequestFilterQueryProps): Observable<sharedApi.IHttpResponseEquipment[]> {
    const params = new HttpParams().set('$filter', this.makeFilterByObj(queryParams)).set('$format', 'json');
    const fullUrl = this.makeUrl('getEquipment');
    return this.http.get<sharedApi.IHttpResponseEquipment[]>(fullUrl, { params }).pipe(map((res) => res ?? []));
  }

  public getOneEquipment$(queryParams: ISapRequestFilterQueryProps): Observable<sharedApi.IHttpResponseEquipment | undefined> {
    const params = new HttpParams().set('$filter', this.makeFilterByObj(queryParams)).set('$format', 'json');
    const fullUrl = this.makeUrl('getEquipment');
    //we get an array back, but we only return the first value from it
    return this.http.get<sharedApi.IHttpResponseEquipment[]>(fullUrl, { params }).pipe(map((res) => res[0]));
  }

  public getNonbillable$(): Observable<sharedApi.IHttpResponseNonBill[]> {
    const fullUrl = this.makeUrl('getNonbill');
    return this.http.get<sharedApi.IHttpResponseNonBill[]>(fullUrl).pipe(
      map((res) =>
        (res ?? []).map((x) => {
          //@ts-expect-error - I don't know why we do this, Absence is a boolean and doesn't need to be parsed, but I'm keeping it here for compatibility. We can remove this when we fully switch over to Nest
          x.Absence = JSON.parse(x.Absence);
          return x;
        }),
      ),
    );
  }

  public getTimesheets$(queryParams: ISapRequestFilterQueryProps): Observable<IExtDocument[]> {
    const params = new HttpParams().set('$filter', this.makeFilterByObj(queryParams));
    const fullUrl = this.makeUrl('getTimesheet');
    return this.http.get<IExtDocument[]>(fullUrl, { params }).pipe(map((res) => res ?? []));
  }

  public getHomePage$(owner: string): Observable<sharedApi.IWebserverResponseHome> {
    const fullUrl = this.makeUrl(`time-period/${owner}`);
    return this.http.get<sharedApi.IWebserverResponseHome>(fullUrl);
  }

  public updateCreateContractDefault$(body: sharedApi.IConditionToDefault): Observable<boolean> {
    const fullUrl = this.makeUrl('getContractDefaults');
    return this.http.put<boolean>(fullUrl, body);
  }

  public makeTimesheet$(body: ISapRequestFilterQueryProps): Observable<IExtDocument> {
    const fullUrl = this.makeUrl('timesheet');
    return this.http.post<IExtDocument>(fullUrl, body);
  }

  public updateExtDocument$(body: Partial<IExtDocument>): Observable<boolean> {
    const fullUrl = this.makeUrl('timesheet');
    return this.http.patch<boolean>(fullUrl, body);
  }

  public deleteExtDocument$(id: string): Observable<boolean> {
    const fullUrl = this.makeUrl('timesheet');
    return this.http.delete<boolean>(fullUrl, { body: { Documentno: id } });
  }

  public buildTimesheet$(documentNumber: string, status: TimesheetStatusEnum): Observable<sharedApi.IHttpResponseBuildTimesheet> {
    const fullUrl = this.makeUrl('buildTimesheet');
    return this.http.post<sharedApi.IHttpResponseBuildTimesheet>(fullUrl, { number: documentNumber, status });
  }

  public logError$(
    message: string,
    serverError?: IHttpErrorResponseObject,
    url?: string,
  ): Observable<sharedApi.ITimberTrapLogResponse> {
    let severity: sharedApi.ITimberTrapSeverity = 4;
    if (serverError != null) {
      severity = 3; //more important if we have an error object
      if (
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion, @typescript-eslint/no-unnecessary-type-assertion -- Not changing ECC, instead just inserting non-null
        SAP_UNAVAILABLE_ERROR_CODES.includes(serverError.code!) ||
        SAP_UNAVAILABLE_MESSAGE_PARTIALS.some((p) => serverError.technicalMessage?.toLowerCase().includes(p))
      ) {
        severity = 1; //It looks like SAP is down, so bump up the severity to the max
      }
    }
    if (!environment.production) severity = 5; // if not production, it's even less important, so we just log it

    const fullObj: sharedApi.ITimberTrapRequestBody = {
      application: environment.application,
      version: environment.appVersion,
      environment: environment.environmentName,
      severity,
      userName: this.logRocketService.userName,
      user: checkLocalStorage<string>(LocalStorageKeyEnum.UserId) ?? '',
      eventName: url,
      custom: this.logRocketService.url,
      message,
    };

    //If this is a server-side error, add as many details as we have
    if (serverError != null) {
      fullObj.custom2 = serverError.technicalMessage;
      fullObj.errorCode = serverError.code;
      if (serverError.odataDetails != null) fullObj.custom3 = JSON.stringify(serverError.odataDetails, null, 2);
      console.error('Back-End Service Error!', serverError);
    }

    return this.http.post<sharedApi.ITimberTrapLogResponse>(`${this.timberTrapUrl}log`, fullObj, {
      headers: new HttpHeaders().set('Authorization', environment.timberTrapKey),
    });
  }

  public signOut$(): Observable<unknown> {
    const fullUrl = this.makeUrl('logoff');
    return this.http.get<unknown>(fullUrl);
  }

  public getEmployeeManager$(employeeNumber: string): Observable<sharedApi.IHttpResponseEmployeeManager> {
    const cachedEmpManager = this.employeeManagerCache[employeeNumber];
    if (cachedEmpManager != null) return of(cachedEmpManager);
    const fullUrl = this.makeUrl(`getManagerForEmployee/${employeeNumber}`);
    return this.http.get<sharedApi.IHttpResponseEmployeeManager>(fullUrl).pipe(
      tap((res) => {
        this.employeeManagerCache[employeeNumber] = res;
      }),
    );
  }

  public getTimeEval$(body: sharedApi.IHttpRequestPayrollTimeEval): Observable<sharedApi.IPayrollTimeEvalResult[]> {
    const fullUrl = this.makeUrl('timeEval');
    return this.http
      .post<sharedApi.IHttpResponsePayrollTimeEvalResult>(fullUrl, body)
      .pipe(map((res) => res.ResultSet?.results ?? []));
  }

  public getTorEntries$(
    postObj: sharedApi.IHttpRequestPayrollTimeOffRequest,
  ): Observable<sharedApi.IPayrollTimeOffRequestResultSetResult[]> {
    const fullUrl = this.makeUrl('tor');
    return this.http
      .post<sharedApi.IHttpResponsePayrollTimeOffRequest>(fullUrl, postObj)
      .pipe(map((res) => res.ResultSet?.results ?? []));
  }

  public getCostCenter$(costCenter: string): Observable<sharedApi.IHttpResponseCostCenter | undefined> {
    const fullUrl = this.makeUrl(`getCostCenters`);
    const params = new HttpParams().set('$filter', this.makeFilterByObj({ CostCtr: costCenter }));
    return this.http
      .get<sharedApi.IHttpResponseCostCenter[]>(fullUrl, { params: params })
      .pipe(map((value) => value[0] ?? undefined));
  }

  public getApproverData$(empID: string): Observable<sharedApi.IWebServerResponseApprover> {
    const fullUrl = this.makeUrl(`approver/${empID}`);
    return this.http.get<sharedApi.IWebServerResponseApprover>(fullUrl);
  }

  private makeUrl(suffix: string) {
    return `${environment.webServerUrl}api/${suffix}`;
  }

  private makeFilterByObj(obj: ISapRequestFilterQueryProps /*| ISapFilterQueryPropsForTimesheetHeader*/): string {
    let str = '';
    Object.keys(obj).forEach((key, index) => {
      const value = obj[key];
      if (Array.isArray(value)) {
        str += this.handleFilterArrayCase(key, value, index, 'eq');
      } else if (value != null) {
        str += this.handleFilterStringCase(key, value, index, 'eq');
      }
    });
    return str;
  }

  private makeComplexFilterByObj(filterArray: internal.IComplexFilterItem[]): string {
    let str = '';
    filterArray.forEach((item, index) => {
      if (Array.isArray(item.value)) {
        str += this.handleFilterArrayCase(item.name, item.value, index, item.operator);
      } else {
        str += this.handleFilterStringCase(item.name, item.value, index, item.operator);
      }
    });
    return str;
  }

  private handleFilterArrayCase(key: string, arrayParam: string[], loopCount: number, operator: string): string {
    let str = '';
    arrayParam.forEach((value, index) => {
      let rawOrQuotedValue = value;
      if (!/datetime/gim.test(value)) {
        rawOrQuotedValue = `'${value}'`;
      }
      str += index === 0 ? `${key} ${operator} ${rawOrQuotedValue}` : ` or ${key} ${operator} ${rawOrQuotedValue}`;
    });
    return loopCount > 0 ? ` and (${str})` : `(${str})`;
  }

  private handleFilterStringCase(key: string, value: string, index: number, operator: string): string {
    let rawOrQuotedValue = value;
    if (!/datetime/gim.test(value)) {
      rawOrQuotedValue = `'${value}'`;
    }

    if (key.trim() === '') {
      return '';
    } else {
      return index === 0 ? `${key} ${operator} ${rawOrQuotedValue}` : ` and ${key} ${operator} ${rawOrQuotedValue}`;
    }
  }
}
